import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "relative pb-[0.8rem]" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = {
  key: 0,
  xmlns: "http://www.w3.org/2000/svg",
  width: "20",
  height: "20",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
}
const _hoisted_4 = {
  key: 1,
  xmlns: "http://www.w3.org/2000/svg",
  width: "20",
  height: "20",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
}
const _hoisted_5 = { class: "flex justify-end mb-[2rem]" }
const _hoisted_6 = {
  key: 0,
  class: "text-red-600 text-[1.4rem] mb-4"
}
const _hoisted_7 = {
  key: 1,
  type: "button",
  disabled: "",
  class: "w-full bg-gray-400 text-white text-[1.6rem] font-medium py-[1.4rem] rounded-[10rem] cursor-not-allowed"
}
const _hoisted_8 = { class: "mt-[2.4rem] pt-[2.4rem] border-t border-gray-200 text-center" }
const _hoisted_9 = { class: "text-[1.4rem] md:text-[1.6rem] text-gray-600" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "text-[1.4rem] text-gray-600 mb-4" }
const _hoisted_12 = {
  key: 0,
  class: "text-red-600 text-[1.4rem] mt-2"
}
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "text-[1.4rem] text-gray-600" }

import { ref, computed, onBeforeUnmount } from "vue";
import type { ComponentPublicInstance } from "vue";
import { useI18n } from "vue-i18n";
import InputField from "@/components/input/inputField.vue";
import CustomButton from "@/components/buttons/customButton.vue";
import CoreModal from "@/components/modal/CoreModal.vue";
import CustomerHelper from "@/helpers/customerHelper";
import { resetPasswordRequest } from "@/services/medkusApi/publiccustomersService";

type InputFieldInstance = ComponentPublicInstance & {
  updateValue: (payload: { target: { value: string } }) => void;
  isInvalid: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginForm',
  emits: ["login-success", "go-to-register"],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t } = useI18n();

const emit = __emit;

// Form state
const email = ref("");
const password = ref("");
const showPassword = ref(false);
const showError = ref(false);
const errorMessage = ref("");
const isSubmitting = ref(false);

// Lockout state
const failedAttempts = ref(0);
const lockoutSeconds = ref(0);
let lockoutTimer: ReturnType<typeof setInterval> | null = null;

const isLocked = computed(() => lockoutSeconds.value > 0);

// Forgot password state
const showForgotPassword = ref(false);
const forgotEmail = ref("");
const forgotError = ref("");
const forgotPasswordSent = ref(false);

// Template refs
const emailField = ref<InputFieldInstance | null>(null);
const passwordField = ref<InputFieldInstance | null>(null);

const startLockout = (seconds: number) => {
  if (lockoutTimer) clearInterval(lockoutTimer);
  lockoutSeconds.value = seconds;
  lockoutTimer = setInterval(() => {
    lockoutSeconds.value--;
    if (lockoutSeconds.value <= 0) {
      clearInterval(lockoutTimer!);
      lockoutTimer = null;
    }
  }, 1000);
};

onBeforeUnmount(() => {
  if (lockoutTimer) clearInterval(lockoutTimer);
});

const reset = () => {
  email.value = "";
  password.value = "";
  showPassword.value = false;
  showError.value = false;
  errorMessage.value = "";
  isSubmitting.value = false;
  if (lockoutTimer) {
    clearInterval(lockoutTimer);
    lockoutTimer = null;
  }
  lockoutSeconds.value = 0;
  failedAttempts.value = 0;
  showForgotPassword.value = false;
  forgotEmail.value = "";
  forgotError.value = "";
  forgotPasswordSent.value = false;
};

__expose({ reset });

const forceValidateFields = () => {
  const payload = (value: string) => ({ target: { value } });
  emailField.value?.updateValue(payload(email.value));
  passwordField.value?.updateValue(payload(password.value));
  return !emailField.value?.isInvalid && !passwordField.value?.isInvalid;
};

const handleSubmit = async () => {
  if (isLocked.value || isSubmitting.value) return;

  showError.value = false;
  errorMessage.value = "";

  if (!forceValidateFields()) return;

  try {
    isSubmitting.value = true;
    const result = await CustomerHelper.loginWithCredentials(
      email.value,
      password.value,
    );

    if (result.success) {
      window.dispatchEvent(new CustomEvent("market-login-success"));
      emit("login-success");
      return;
    }

    if (result.response?.rateLimited === true) {
      failedAttempts.value = 0;
      startLockout(60);
      showError.value = true;
      errorMessage.value = t("Too many attempts. Please wait a moment.");
      return;
    }

    failedAttempts.value++;
    startLockout(failedAttempts.value * 5);
    showError.value = true;
    errorMessage.value = result.message ?? t("User Login failed");
  } catch {
    failedAttempts.value++;
    startLockout(failedAttempts.value * 5);
    showError.value = true;
    errorMessage.value = t("Login failed");
  } finally {
    isSubmitting.value = false;
  }
};

const onForgotPasswordClose = (val: boolean) => {
  if (!val) {
    forgotEmail.value = "";
    forgotError.value = "";
    forgotPasswordSent.value = false;
  }
};

const submitForgotPassword = async () => {
  forgotError.value = "";
  if (!forgotEmail.value || !forgotEmail.value.includes("@")) {
    forgotError.value = t("Please enter a valid email.");
    return;
  }
  try {
    await resetPasswordRequest({ email: forgotEmail.value });
  } catch {
    // Intencionado: no revelar si el email existe o no
  }
  forgotPasswordSent.value = true;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("form", {
      onSubmit: _withModifiers(handleSubmit, ["prevent"])
    }, [
      _createVNode(InputField, {
        class: "pb-[2.4rem] pt-[2.4rem]",
        modelValue: email.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
        label: _ctx.$t('Email *'),
        placeholder: _ctx.$t('Email'),
        required: "",
        inputType: "email",
        customInputClass: "mt-4",
        ref_key: "emailField",
        ref: emailField,
        inputProps: { autocomplete: 'email', name: 'email' }
      }, null, 8, ["modelValue", "label", "placeholder"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(InputField, {
          modelValue: password.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((password).value = $event)),
          label: _ctx.$t('Password *'),
          placeholder: _ctx.$t('Password'),
          customInputClass: "mt-4 pr-[4.8rem]",
          inputType: showPassword.value ? 'text' : 'password',
          required: "",
          ref_key: "passwordField",
          ref: passwordField,
          inputProps: { autocomplete: 'current-password', name: 'password' }
        }, null, 8, ["modelValue", "label", "placeholder", "inputType"]),
        _createElementVNode("button", {
          type: "button",
          class: "absolute right-[1.6rem] bottom-[2.4rem] flex items-center justify-center text-gray-500 hover:text-black transition-colors",
          "aria-label": showPassword.value ? _ctx.$t('Hide password') : _ctx.$t('Show password'),
          onClick: _cache[2] || (_cache[2] = ($event: any) => (showPassword.value = !showPassword.value))
        }, [
          (showPassword.value)
            ? (_openBlock(), _createElementBlock("svg", _hoisted_3, [...(_cache[7] || (_cache[7] = [
                _createElementVNode("path", { d: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" }, null, -1),
                _createElementVNode("line", {
                  x1: "1",
                  y1: "1",
                  x2: "23",
                  y2: "23"
                }, null, -1)
              ]))]))
            : (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[8] || (_cache[8] = [
                _createElementVNode("path", { d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" }, null, -1),
                _createElementVNode("circle", {
                  cx: "12",
                  cy: "12",
                  r: "3"
                }, null, -1)
              ]))]))
        ], 8, _hoisted_2)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("a", {
          href: "#",
          class: "text-[1.3rem] text-gray-500 underline hover:text-black transition-colors",
          onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (showForgotPassword.value = true), ["prevent"]))
        }, _toDisplayString(_ctx.$t("Forgot your password?")), 1)
      ]),
      (showError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(errorMessage.value), 1))
        : _createCommentVNode("", true),
      (isLocked.value)
        ? (_openBlock(), _createElementBlock("button", _hoisted_7, _toDisplayString(_ctx.$t("Wait")) + " " + _toDisplayString(lockoutSeconds.value) + "s ", 1))
        : (_openBlock(), _createBlock(CustomButton, {
            key: 2,
            label: isSubmitting.value ? _ctx.$t('Login') + '...' : _ctx.$t('Login'),
            type: "submit"
          }, null, 8, ["label"])),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("p", _hoisted_9, [
          _createTextVNode(_toDisplayString(_ctx.$t("Don't have an account yet?")) + " ", 1),
          _createElementVNode("a", {
            href: "#",
            class: "ml-1 font-semibold text-black underline hover:opacity-70 transition-opacity",
            onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (emit('go-to-register')), ["prevent"]))
          }, _toDisplayString(_ctx.$t("Sign up here")), 1)
        ])
      ])
    ], 32),
    _createVNode(CoreModal, {
      modelValue: showForgotPassword.value,
      "onUpdate:modelValue": [
        _cache[6] || (_cache[6] = ($event: any) => ((showForgotPassword).value = $event)),
        onForgotPasswordClose
      ],
      title: _ctx.$t('Recover your password'),
      "close-on-overlay": true
    }, {
      default: _withCtx(() => [
        (!forgotPasswordSent.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$t(
              "Enter your email and we will send you a link to reset your password.",
            )), 1),
              _createVNode(InputField, {
                modelValue: forgotEmail.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((forgotEmail).value = $event)),
                label: _ctx.$t('Email *'),
                placeholder: _ctx.$t('Email'),
                required: "",
                inputType: "email",
                customInputClass: "mt-4",
                ref: "forgotEmailField"
              }, null, 8, ["modelValue", "label", "placeholder"]),
              (forgotError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(forgotError.value), 1))
                : _createCommentVNode("", true)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createElementVNode("p", _hoisted_14, _toDisplayString(_ctx.$t(
              "If the email exists, you will receive a link to reset your password shortly.",
            )), 1)
            ]))
      ]),
      actions: _withCtx(() => [
        (!forgotPasswordSent.value)
          ? (_openBlock(), _createBlock(CustomButton, {
              key: 0,
              label: _ctx.$t('Send'),
              class: "px-[4.8rem] py-[1.8rem] text-[1.8rem] font-medium",
              onClick: submitForgotPassword
            }, null, 8, ["label"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})